/**
 * **C11 · The sponsor invoice, the sponsor receipt and the donation receipt** (Stream C,
 * Documents.dc.html): half-letter papers laid out by the `sponsor_papers` engine from Money's
 * receivable, the sponsorship's level and pledge day (Sponsors), the gift and the year's tax line
 * (Commerce), signed by the finance director named in Roles. Scored against the drawing by walk 99g.
 */
import type { ReactNode } from 'react';
import {
  FestivalClock,
  donationReceipt,
  letterDay,
  shortDay,
  sponsorInvoice,
  sponsorReceipt,
  type SponsorBill,
} from '@cider/domain/engines';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';
import { holder, type RoleRow } from './aod-package.js';

interface Receivable {
  number: string;
  festivalYear: number;
  organizationName: string;
  pledgeId: string | null;
  what: string;
  amountCents: number;
  paidCents: number;
  owedCents: number;
  dueOn: string;
  payments: { at: string; how: string; reference: string | null }[];
  payPage?: string | null;
}

/** A day as the papers print it (`May 12, 2027`), from a calendar date or an instant. */
const day = (at: string): string =>
  letterDay(/^\d{4}-\d{2}-\d{2}$/.test(at) ? at : FestivalClock.calendarDay(new Date(at)));

function SponsorPaper({
  fy,
  id,
  which,
}: {
  fy: number;
  id: string;
  which: 'invoice' | 'receipt';
}): ReactNode {
  const bill = useLoad<Receivable>(`/money/receivables/${encodeURIComponent(id)}`);
  const pledge = bill.data?.pledgeId ?? null;
  const record = useLoad<{ pledge: { level: string | null; pledgedAt: string | null } }>(
    pledge ? `/marketing/sponsorships/${encodeURIComponent(pledge)}` : null,
  );
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(fy - 1)}`);
  const ready =
    (bill.data !== null || bill.error !== null) &&
    (pledge === null || record.data !== null || record.error !== null) &&
    (roles.data !== null || roles.error !== null);
  usePrinted(`doc-sponsor-${which}`, ready, { ref: id, year: fy - 1 });
  if (!ready) return <p>Reading the invoice…</p>;
  const b = bill.data;
  const last = b?.payments.at(-1);
  const sponsorBill: SponsorBill | null = b
    ? {
        number: b.number,
        festivalYear: b.festivalYear,
        organizationName: b.organizationName,
        what: b.what,
        amountCents: b.amountCents,
        paidCents: b.paidCents,
        owedCents: b.owedCents,
        due: shortDay(b.dueOn),
        received: last
          ? `${day(last.at)} · ${last.how}${last.reference ? ` ${last.reference}` : ''}`
          : null,
        payPage: b.payPage ?? null,
      }
    : null;
  const input = {
    bill: sponsorBill,
    level: record.data?.pledge.level ?? null,
    pledged: record.data?.pledge.pledgedAt ? day(record.data.pledge.pledgedAt) : null,
    financeDirector: holder(roles.data, 'finance_director'),
  };
  return (
    <DrawnPages
      pages={[which === 'invoice' ? sponsorInvoice(input) : sponsorReceipt(input)]}
      size="half"
    />
  );
}

/** `/print/doc/sponsor-invoice:<fy>:<receivable id>`. */
export function SponsorInvoice({ fy, id }: { fy: number; id: string }): ReactNode {
  return <SponsorPaper fy={fy} id={id} which="invoice" />;
}

/** `/print/doc/sponsor-receipt:<fy>:<receivable id>`. */
export function SponsorReceipt({ fy, id }: { fy: number; id: string }): ReactNode {
  return <SponsorPaper fy={fy} id={id} which="receipt" />;
}

/** `/print/doc/donation-receipt:<fy>:<donation id>` — the festival's gift, with the year's tax line. */
export function DonationReceipt({ fy, id }: { fy: number; id: string }): ReactNode {
  const gifts = useLoad<{
    rows: { id: string; name: string; cents: number }[];
    taxLine: string | null;
  }>(`/commerce/donations?year=${String(fy - 1)}`);
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(fy - 1)}`);
  const ready = [gifts, roles].every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-donation-receipt', ready, { ref: id, year: fy - 1 });
  if (!ready) return <p>Reading the gift…</p>;
  const g = gifts.data?.rows.find((r) => r.id === id) ?? null;
  const page = donationReceipt({
    gift: g ? { name: g.name, cents: g.cents } : null,
    // The gift's day is not in Commerce's answer yet (WORKLIST K12).
    on: null,
    taxLine: gifts.data?.taxLine ?? null,
    financeDirector: holder(roles.data, 'finance_director'),
  });
  return <DrawnPages pages={[page]} size="half" />;
}
